import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { notificationAPI } from '../lib/api';

const NotificationContext = createContext(null);

// Get WebSocket URL from backend URL
const getWebSocketUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Convert http(s) to ws(s)
  return backendUrl.replace(/^http/, 'ws');
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getNotifications({ limit: 50 });
      setNotifications(res.data || []);
      setUnreadCount(res.data?.filter(n => !n.is_read).length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, skipping WebSocket connection');
      return;
    }

    // Use /api/ws prefix for Kubernetes ingress compatibility
    const wsUrl = `${getWebSocketUrl()}/api/ws/notifications/${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'notification') {
            // Add new notification to state
            const notification = data.notification;
            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification
            toast.info(notification.title, {
              description: notification.message,
              duration: 5000,
              action: notification.data?.reference_id ? {
                label: 'View',
                onClick: () => {
                  // Navigate to the relevant page based on reference_type
                  const type = notification.data?.reference_type;
                  const id = notification.data?.reference_id;
                  if (type === 'commission_claim') {
                    window.location.href = `/claims/${id}`;
                  }
                }
              } : undefined
            });
          } else if (data.type === 'unread_count') {
            setUnreadCount(data.count);
          } else if (data.type === 'marked_read') {
            setNotifications(prev => 
              prev.map(n => 
                n.id === data.notification_id ? { ...n, is_read: true } : n
              )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Reconnect after delay (if not intentionally closed)
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, []);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    // Send via WebSocket if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ 
        type: 'mark_read', 
        notification_id: notificationId 
      }));
    } else {
      // Fallback to REST API
      try {
        await notificationAPI.markRead(notificationId);
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Request unread count via WebSocket
  const refreshUnreadCount = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'get_unread_count' }));
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchNotifications();
      connect();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect, fetchNotifications]);

  // Listen for auth changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          connect();
          fetchNotifications();
        } else {
          disconnect();
          setNotifications([]);
          setUnreadCount(0);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect, fetchNotifications]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      isConnected,
      markAsRead,
      markAllAsRead,
      refreshUnreadCount,
      fetchNotifications,
      connect,
      disconnect
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
